/**
 * Books registry — admin page. The normalized source for textbook-origin
 * questions (see docs/FeatureDesigns/question-pools-and-entitlement/question-source-type-target.md). List + create/
 * edit modal + delete. Questions reference a book by id via BookRef.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api/adminApi';
import type { Book, Grade } from '../types';
import { PUBLISHER_OPTIONS } from '../types';
import { SUBJECT_OPTIONS, REGISTRABLE_PROGRAMME_OPTIONS, GRADE_OPTIONS } from '@padcanvas/shared';
import { Modal, Input, Select, Button, DataTable, type ColumnDef } from '@shared-ui/components';
import './Books.css';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function Books() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [modal, setModal] = useState<Partial<Book> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Book | null>(null);

  const { data, isLoading, error } = useQuery({ queryKey: ['books'], queryFn: () => adminApi.listBooks() });
  const books = data?.books ?? [];

  const saveBook = useMutation({
    mutationFn: (b: Partial<Book>) => adminApi.saveBook(b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['books'] }); setModal(null); },
  });
  const deleteBook = useMutation({
    mutationFn: (id: string) => adminApi.deleteBook(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['books'] }); setConfirmDelete(null); },
  });

  const m = modal;

  const columns: ColumnDef<Book>[] = [
    { key: 'title', header: 'Title', render: b => <span style={{ fontWeight: 500 }}>{b.title}</span> },
    { key: 'author', header: 'Author', render: b => b.author || '—' },
    { key: 'publisher', header: 'Publisher', render: b => b.publisher || '—' },
    { key: 'subject', header: 'Subject', render: b => (b.subject ? cap(b.subject) : '—') },
    { key: 'board', header: 'Board', render: b => b.board || '—' },
    { key: 'grades', header: 'Class', render: b => (b.grades?.length ? b.grades.join(', ') : '—') },
    { key: 'edition', header: 'Edition', render: b => b.edition || '—' },
    {
      key: 'actions', header: '', width: '1%', render: b => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
          <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); setModal({ ...b }); }}>Edit</Button>
          <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); setConfirmDelete(b); }}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <h1 style={{ margin: 0 }}>Books</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 13, maxWidth: 620 }}>
            The book registry — a reusable source for textbook-origin questions. Questions reference a book here instead of retyping publisher/title, so nothing drifts.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setModal({ isActive: true })}>+ New Book</Button>
      </div>

      {error && <div style={{ color: 'var(--color-error)' }}>{(error as Error).message}</div>}

      {!error && (
        <DataTable<Book>
          columns={columns}
          data={books}
          rowKey={b => b.id}
          rowClassName={b => (b.isActive === false ? 'book-inactive-row' : '')}
          onRowClick={b => navigate(`/books/${b.id}`)}
          loading={isLoading}
          bordered
          emptyMessage="No books yet. Add one to reference from textbook questions."
        />
      )}

      {/* Create / edit modal — two-column form */}
      <Modal isOpen={!!m} onClose={() => setModal(null)} title={m?.id ? 'Edit Book' : 'New Book'} size="md">
        {m && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ gridColumn: '1 / -1' }}>Title
              <Input value={m.title ?? ''} onChange={(e) => setModal({ ...m, title: e.target.value })} placeholder="Concepts of Physics Vol 1" />
            </label>
            <label>Author (optional)
              <Input value={m.author ?? ''} onChange={(e) => setModal({ ...m, author: e.target.value })} placeholder="HC Verma" />
            </label>
            <label>Publisher (optional)
              <Select value={m.publisher ?? ''} onChange={(e) => setModal({ ...m, publisher: (e.target.value || undefined) as Book['publisher'] })}>
                <option value="">— none —</option>
                {PUBLISHER_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </Select>
            </label>
            <label>Board (optional)
              <Select value={m.board ?? ''} onChange={(e) => setModal({ ...m, board: (e.target.value || undefined) as Book['board'] })}>
                <option value="">— none —</option>
                {REGISTRABLE_PROGRAMME_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
              </Select>
            </label>
            <label>Subject (optional)
              <Select value={m.subject ?? ''} onChange={(e) => setModal({ ...m, subject: (e.target.value || undefined) as Book['subject'] })}>
                <option value="">— none —</option>
                {SUBJECT_OPTIONS.map(s => <option key={s} value={s}>{cap(s)}</option>)}
              </Select>
            </label>
            <label>Edition (optional)
              <Input value={m.edition ?? ''} onChange={(e) => setModal({ ...m, edition: e.target.value })} placeholder="2023 / 7th ed." />
            </label>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ marginBottom: 6 }}>Class (optional)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {GRADE_OPTIONS.map(c => {
                  const selected = (m.grades ?? []).includes(c as Grade);
                  return (
                    <label key={c} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                      border: `1px solid ${selected ? 'var(--color-accent, #4f8cff)' : 'var(--border-color, #333)'}`,
                      borderRadius: 6, cursor: 'pointer', fontSize: 13,
                      color: selected ? 'var(--color-accent, #4f8cff)' : 'var(--text-secondary)',
                    }}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => {
                          const cur = new Set(m.grades ?? []);
                          if (e.target.checked) cur.add(c as Grade); else cur.delete(c as Grade);
                          const next = (GRADE_OPTIONS as readonly string[]).filter(x => cur.has(x as Grade)) as Grade[];
                          setModal({ ...m, grades: next.length ? next : undefined });
                        }}
                      />
                      Class {c}
                    </label>
                  );
                })}
              </div>
            </div>
            <label style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={m.isActive ?? true} onChange={(e) => setModal({ ...m, isActive: e.target.checked })} />
              Active
            </label>
            {saveBook.error ? <div style={{ gridColumn: '1 / -1', color: 'var(--color-error)', fontSize: 13 }}>{(saveBook.error as Error).message}</div> : null}
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="secondary" size="sm" onClick={() => setModal(null)}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={!m.title || saveBook.isPending} onClick={() => saveBook.mutate(m)}>
                {m.id ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete book?" size="sm">
        {confirmDelete && (
          <div style={{ display: 'grid', gap: 14 }}>
            <div>Delete <strong>{confirmDelete.title}</strong>? Questions that already reference it keep their denormalized book name, but the registry entry is removed.</div>
            {deleteBook.error ? <div style={{ color: 'var(--color-error)', fontSize: 13 }}>{(deleteBook.error as Error).message}</div> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={deleteBook.isPending} onClick={() => deleteBook.mutate(confirmDelete.id)}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
